import union from "@turf/union";
import { Feature, Polygon, MultiPolygon } from "@turf/helpers";
import buffer from "@turf/buffer";
import { Geometry } from "react-migration/domains/ownership/typings/Geometry";
import { isEmpty } from "lodash";

/**
 * Takes an array of polygons and returns a merged polygon using turf union
 * @param geometries: Geometry[]
 * @returns Geometry
 */
export const generateMergedPolygon = (geometries: Geometry[]): Geometry => {
  const turfGeometries = geometries
    .map((geo) => ({ type: "Feature", geometry: geo } as Feature<Geometry>))
    .filter((geo) => geo.geometry?.coordinates && !isEmpty(geo.geometry?.coordinates));

  if (turfGeometries.length === 0) {
    return { type: "Polygon", coordinates: [] } as Geometry;
  }

  try {
    const merged = union(...(turfGeometries as Feature<Polygon>[])) as Feature<Polygon | MultiPolygon>;
    return { type: merged.geometry?.type, coordinates: merged.geometry?.coordinates } as Geometry;
  } catch {
    // Fallback: progressive union with small buffer
    try {
      const buffered = turfGeometries.map((geo) => buffer(geo, 0.000001, { units: "degrees" }));

      if (buffered.length === 0) {
        throw new Error("No buffered geometries available");
      }

      let merged = buffered[0] as Feature<Polygon | MultiPolygon>;
      for (let i = 1; i < buffered.length; i++) {
        merged = union(merged as Feature<Polygon>, buffered[i] as Feature<Polygon>) as Feature<Polygon | MultiPolygon>;
      }
      return { type: merged.geometry?.type, coordinates: merged.geometry?.coordinates } as Geometry;
    } catch (error) {
      return {
        type: "Polygon",
        coordinates: [],
      } as Geometry;
    }
  }
};
